import { Fund } from "creso-core/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from "web-design-system";
import { ChevronDown } from "lucide-react";
import { useCompareFundPortfolios } from "creso-core/hooks";
import { useMemo } from "react";

const MAX_HOLDINGS = 10;

export function TopHoldings({
  funds,
  fundIds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  fundIds: string[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const { data: portfolios } = useCompareFundPortfolios(fundIds);

  const rowCount = useMemo(() => {
    const max = portfolios.reduce((acc, portfolio) => {
      return Math.max(acc, portfolio?.top_holdings?.length ?? 0);
    }, 0);
    return Math.min(max, MAX_HOLDINGS);
  }, [portfolios]);

  // CSS Grid template shared by every row — columns never resize based on content
  const gridCols = `${labelWidth}px repeat(${fundIds.length}, 1fr) ${variant === "default" ? "80px" : "0"}`;

  const grid = (
    <div
      className={cn(
        "bg-background",
        variant === "report" ? "border-t-0" : "border-t border-border"
      )}
    >
      {Array.from({ length: rowCount }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className="grid border-b border-border last:border-b-0"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div className="bg-muted" />
          {fundIds.map((fundId, colIdx) => {
            const portfolio = portfolios.find((p) => p?.fund === fundId);
            const holding = portfolio?.top_holdings?.[rowIdx];
            return (
              <div
                key={fundId}
                className={cn(
                  "flex items-center justify-between px-5 py-3 gap-3 min-w-0",
                  colIdx < funds.length - 1 && "border-r border-border"
                )}
              >
                {holding ? (
                  <>
                    <span className="text-sm line-clamp-2 min-w-0">
                      {holding.Name}
                    </span>
                    <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                      {parseFloat(holding.Weighting).toFixed(2)}%
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
            );
          })}
          <div />
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg overflow-hidden border border-border group"
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full justify-between items-center px-5 py-4",
            variant === "report" && "pointer-events-none"
          )}
        >
          <span className="font-semibold text-base">Top 10 holdings</span>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>
        <CollapsibleContent>{grid}</CollapsibleContent>
      </Collapsible>
    </div>
  );
}
